/**
 * 벌점 상세보기 모달
 *
 * 상벌점 기록에서 한 건을 누르면 "왜 이 벌점이 붙었는지"를 그대로 보여준다.
 *   - 몇 시까지 왔어야 하는지 / 실제 몇 시에 왔는지 / 몇 분 늦었는지
 *   - 그날 입·퇴실 기록 전체
 *   - 정규 구간인지 자율학습 구간인지
 *
 * 회원관리 상세 모달의 벌점 탭에서도 같은 컴포넌트를 쓴다.
 */
import React, { useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { API_BASE_URL } from '../../../../config/api';
import { penaltySourceBadge } from '../../utils/penaltySource';

// PenaltyService 와 동일한 규칙 (벌점 API 는 /api/penalties prefix)
const getApiUrl = (path: string) => `${API_BASE_URL === '' ? '/api' : API_BASE_URL}${path}`;
const getToken = () =>
  localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken') || '';

interface DetailRecord {
  id: string;
  student_name: string | null;
  penalty_type: string;
  point_type: string;
  points: number;
  original_points: number | null;
  reduced_points: number | null;
  reduction_reason: string | null;
  reason: string;
  occurred_date: string | null;
  occurred_time: string | null;
  is_auto: boolean;
  is_cancelled: boolean;
  cancel_reason: string | null;
  reference_type: string | null;
  segment: 'regular' | 'self_study';
  segment_label: string;
}

interface DetailContext {
  expected_time: string | null;
  first_arrival: string | null;
  minutes_late: number | null;
  is_exempt: boolean;
  attendance_spans: Array<{ start: string | null; end: string | null }>;
  schedule_source: string | null;
  registered_ranges: Array<{ start: string | null; end: string | null }>;
  period_blocks: Array<{
    period_number: number;
    period_name: string;
    start: string;
    end: string;
  }>;
  required_intervals: Array<{ start: string | null; end: string | null }>;
  access_logs: Array<{ time: string | null; action: string; label: string }>;
  late_period: { period_number: number; period_label: string } | null;
  late_periods_today: Array<{
    period_label: string;
    expected: string;
    actual: string;
    minutes_late: number;
  }>;
  self_study_window: { start: string | null; end: string | null } | null;
  expected_end: string | null;
  left_at: string | null;
  minutes_early: number | null;
  leave_kind: 'exit' | 'outing' | null;
  note: string | null;
}

interface Props {
  recordId: string;
  onClose: () => void;
}

const TYPE_LABEL: Record<string, string> = {
  absence: '결석',
  late_arrival: '지각',
  late_return: '쉬는시간 지각',
  early_departure: '조퇴',
};

const PenaltyDetailModal: React.FC<Props> = ({ recordId, onClose }) => {
  const [record, setRecord] = useState<DetailRecord | null>(null);
  const [context, setContext] = useState<DetailContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        const res = await fetch(getApiUrl(`/penalties/records/${recordId}/detail`), {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (!res.ok) {
          const e = await res.json().catch(() => ({ detail: '조회 실패' }));
          throw new Error(e.detail || `HTTP ${res.status}`);
        }
        const data = await res.json();
        setRecord(data.record);
        setContext(data.context);
      } catch (e) {
        setError(e instanceof Error ? e.message : '조회에 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [recordId]);

  const isMerit = record?.point_type === 'merit';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
        {/* 헤더 */}
        <div className="flex items-center justify-between px-5 py-3 border-b sticky top-0 bg-white">
          <h3 className="font-semibold text-gray-900">상벌점 상세</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" title="닫기">
            <XMarkIcon style={{ width: '1.1rem', height: '1.1rem' }} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {loading && <p className="text-sm text-gray-500 py-6 text-center">불러오는 중…</p>}
          {error && <p className="text-sm text-red-600 py-6 text-center">{error}</p>}

          {record && context && (
            <>
              {/* 요약 */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-gray-900">{record.student_name || '학생'}</span>
                <span
                  className={`px-2 py-0.5 text-xs rounded ${
                    isMerit ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'
                  }`}
                >
                  {isMerit ? '상점' : '벌점'} {TYPE_LABEL[record.penalty_type] || record.penalty_type}
                </span>
                <span className={`font-semibold ${isMerit ? 'text-blue-600' : 'text-red-600'}`}>
                  {isMerit ? '+' : '-'}
                  {record.points}점
                </span>
                {record.segment === 'self_study' && (
                  <span className="px-2 py-0.5 text-xs rounded bg-rose-100 text-rose-700">
                    자율학습 구간
                  </span>
                )}
                {(() => {
                  const b = penaltySourceBadge(record.reference_type, record.is_auto);
                  return <span className={`px-2 py-0.5 text-xs rounded ${b.className}`}>{b.label}</span>;
                })()}
                {record.is_cancelled && (
                  <span className="px-2 py-0.5 text-xs rounded bg-gray-200 text-gray-600">취소됨</span>
                )}
              </div>

              {/* 언제·얼마나 */}
              <div className="border rounded-lg divide-y text-sm">
                <Row label="발생일" value={record.occurred_date} />
                {record.penalty_type === 'early_departure' ? (
                  <>
                    <Row label="있어야 했던 시각까지" value={context.expected_end || '—'} />
                    <Row
                      label={context.leave_kind === 'outing' ? '외출한 시각' : '나간 시각'}
                      value={context.left_at || '기록 없음'}
                      strong={!context.left_at}
                    />
                    {context.minutes_early != null && (
                      <Row label="조퇴 시간" value={`${context.minutes_early}분 일찍`} strong />
                    )}
                  </>
                ) : (
                  <>
                    <Row
                      label="와야 했던 시각"
                      value={context.expected_time || '—'}
                    />
                    <Row
                      label="실제 입실 시각"
                      value={context.first_arrival || '입실 기록 없음'}
                      strong={!context.first_arrival}
                    />
                    {context.minutes_late != null && (
                      <Row
                        label="지각 시간"
                        value={`${context.minutes_late}분 늦음`}
                        strong
                      />
                    )}
                  </>
                )}
                {context.late_period && (
                  <Row label="늦은 교시" value={context.late_period.period_label} strong />
                )}
                {record.segment === 'self_study' && context.self_study_window && (
                  <Row
                    label="자율학습 구간"
                    value={`${context.self_study_window.start} ~ ${context.self_study_window.end}`}
                  />
                )}
                <Row label="사유" value={record.reason?.replace('[자율학습]', '').trim()} />
                {record.reduced_points ? (
                  <Row
                    label="감경"
                    value={`${record.original_points ?? '-'}점 → ${record.points}점 (${record.reduction_reason || '사유 없음'})`}
                  />
                ) : null}
                {record.is_cancelled && (
                  <Row label="취소 사유" value={record.cancel_reason || '—'} />
                )}
              </div>

              {/* 그날 늦게 들어온 교시 전체 (여러 교시 늦었을 수 있음) */}
              {context.late_periods_today?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">
                    이 날 늦게 들어온 교시 ({context.late_periods_today.length}개)
                  </p>
                  <div className="border rounded divide-y">
                    {context.late_periods_today.map((p, i) => (
                      <div key={i} className="flex items-center gap-3 px-3 py-1.5 text-sm">
                        <span className="font-medium text-gray-900" style={{ minWidth: '4rem' }}>
                          {p.period_label}
                        </span>
                        <span className="text-gray-600">
                          {p.expected} 시작 → {p.actual} 착석
                        </span>
                        <span className="text-red-600 font-medium ml-auto">
                          {p.minutes_late}분 늦음
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 이 날 스케줄 — ① 학생 기준 입실~퇴실 → ② 그 안에 들어가는 교시 */}
              {(context.attendance_spans?.length > 0 ||
                context.required_intervals.length > 0) && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">이 날 스케줄</p>

                  {context.attendance_spans?.length > 0 && (
                    <div className="mb-2">
                      <p className="text-xs text-gray-500 mb-1">
                        학생이 등록한 시간
                        {context.schedule_source === 'periods' && ' (교시로 선택)'}
                        {context.schedule_source === 'ranges' && ' (시간 직접 입력)'}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {context.attendance_spans.map((iv, i) => (
                          <span
                            key={i}
                            className="px-2.5 py-1 text-sm bg-blue-100 text-blue-800 rounded font-medium"
                          >
                            {iv.start} 입실 ~ {iv.end} 퇴실
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {context.period_blocks?.length > 0 ? (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">
                        포함된 교시 ({context.period_blocks.length}개)
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {context.period_blocks.map((p) => (
                          <span
                            key={p.period_number}
                            className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded"
                          >
                            {p.period_name} {p.start}~{p.end}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : (
                    context.required_intervals.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {context.required_intervals.map((iv, i) => (
                          <span key={i} className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">
                            {iv.start} ~ {iv.end}
                          </span>
                        ))}
                      </div>
                    )
                  )}
                </div>
              )}

              {/* 그날 입·퇴실 기록 */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">이 날 입·퇴실 기록</p>
                {context.access_logs.length === 0 ? (
                  <p className="text-sm text-gray-500">기록이 없습니다.</p>
                ) : (
                  <div className="border rounded max-h-40 overflow-y-auto divide-y">
                    {context.access_logs.map((log, i) => (
                      <div key={i} className="flex items-center gap-3 px-3 py-1.5 text-sm">
                        <span className="text-gray-900 font-medium">{log.time}</span>
                        <span className="text-gray-600">{log.label}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {context.note && (
                <p className="text-xs text-gray-600 bg-gray-50 border rounded p-2">{context.note}</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Row: React.FC<{ label: string; value?: string | null; strong?: boolean }> = ({
  label,
  value,
  strong,
}) => (
  <div className="flex items-start gap-3 px-3 py-2">
    <span className="text-gray-500 shrink-0" style={{ minWidth: '7rem' }}>
      {label}
    </span>
    <span className={strong ? 'text-red-600 font-medium' : 'text-gray-900'}>{value || '—'}</span>
  </div>
);

export default PenaltyDetailModal;
